import type { BulkEditRequest, ContactFieldDto, ContactStatusDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Field, SelectField } from '../../ui/Field.js'
import { count } from '../../ui/format.js'
import { Popover } from '../../ui/Popover.js'
import type { BoardColumn } from './boardColumns.js'
import { Declared, STATES } from './ContactFields.js'

/**
 * Setting one column on everybody ticked.
 *
 * **One column, not a form.** "Select fifty, change Subscribed to
 * Unsubscribed" is what this is for. A form that set three columns at once
 * would make "leave this alone" and "set this to empty" the same gesture, and
 * the difference between them is fifty people's data.
 *
 * **The value is asked for the way that column is asked for anywhere else** —
 * `Declared` for a field, the same three states the contact form offers for a
 * subscription. A control of its own here would be a second answer to what a
 * column may hold, and the one that drifted would be this one, because it is
 * the one nobody opens every day.
 *
 * **Which columns are offered comes from the domain**, not from a list here:
 * `BASE_COLUMNS` carries what sets each of them, and says why a name, an
 * address, a segment and Receiving are not among them. A declared field is
 * always offered, so declaring one never means editing this file.
 *
 * **The button says what it is about to do** — which column, how many people,
 * and whether it is setting or clearing. A bulk action whose button says only
 * "Apply" is one people press without reading, and there is no undo for the
 * values it overwrites.
 */
export function BulkEdit({
  columns,
  fields,
  picked,
  busy,
  onApply,
}: {
  readonly columns: readonly BoardColumn[]
  readonly fields: readonly ContactFieldDto[]
  /** How many are ticked, so the button can say it rather than imply it. */
  readonly picked: number
  readonly busy: boolean
  readonly onApply: (change: BulkEditRequest['change'], column: string) => void
}) {
  const editable = columns.filter((column) => column.bulkEdit !== undefined)
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState(editable[0]?.id ?? '')
  const [value, setValue] = useState<string>(FIRST_STATE)

  const column = editable.find((one) => one.id === chosen) ?? editable[0]
  if (column === undefined) return null

  const field = column.key === undefined ? undefined : fields.find((one) => one.key === column.key)

  /*
   * Changing which column is being set clears the value.
   *
   * A company name carried into a subscription picker is a value that column
   * cannot hold, and the control would silently show its first option while the
   * state said otherwise — the same mistake the board's filter made until
   * `applyPatch` became the one place a field change is handled.
   */
  const pick = (id: string) => {
    setChosen(id)
    setValue(editable.find((one) => one.id === id)?.bulkEdit === 'status' ? FIRST_STATE : '')
  }

  const apply = () => {
    setOpen(false)
    if (column.bulkEdit === 'status') {
      onApply({ column: 'status', value: value as ContactStatusDto }, column.label)
    } else if (column.bulkEdit === 'company') {
      onApply({ column: 'company', value }, column.label)
    } else if (column.key !== undefined) {
      onApply({ column: 'field', key: column.key, value }, column.label)
    }
  }

  return (
    <Popover
      align="right"
      label="Change a column on the contacts you have ticked"
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      panelClassName="w-[22rem] p-3"
      trigger="Edit a column"
      triggerClassName="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:text-t1"
    >
      <SelectField
        disabled={busy}
        id="bulk-column"
        label="Column"
        onChange={pick}
        options={editable.map((one) => [one.id, one.label] as const)}
        value={column.id}
      />

      {column.bulkEdit === 'status' ? (
        <SelectField
          disabled={busy}
          id="bulk-status"
          label={column.label}
          onChange={setValue}
          options={STATES}
          value={value}
        />
      ) : field === undefined ? (
        <Field
          disabled={busy}
          hint="Leave it empty to clear this column."
          id="bulk-company"
          label={column.label}
          onChange={setValue}
          optional
          type="text"
          value={value}
        />
      ) : (
        <Declared busy={busy} field={field} id="bulk-field" onChange={setValue} value={value} />
      )}

      <button
        className="mt-2 w-full rounded-pill bg-brand px-3 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        disabled={busy}
        onClick={apply}
        type="button"
      >
        {value === '' ? 'Clear' : 'Set'} {column.label} for {count(picked)}
      </button>
    </Popover>
  )
}

/** What a subscription picker opens on, and what every untouched one submits. */
const FIRST_STATE = STATES[0]?.[0] ?? 'subscribed'
